import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';

import Grid from '@material-ui/core/Grid';
import EditIcon from '@material-ui/icons/EditOutlined';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Divider from '@material-ui/core/Divider';

import { sqlDateToLabel } from 'helpers/helpers';
import Background from 'components/Layout/Background';
import FormTextField from 'components/FormFields/FormTextField';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import CompanyOwnerModal from './CompanyOwnerModal';
import { CompanyOwnersStore } from 'stores/workflowStores/workflowAMLStore/companyOwnersStore';
import { CompanyOwnersData } from 'stores/workflowStores/workflowAMLStore/model';
import { RootStore } from 'stores/rootStore';
import Address from 'pages/Accounts/Edit/Contract/PersonalData/components/Address';

interface Props {
  accountId: string;
  rootStore: RootStore;
}

const CompanyOwners: React.FC<Props> = ({ accountId, rootStore }) => {
  const { t } = useTranslation();

  const [isOwnerOpen, setIsOwnerOpen] = useState(false);
  const [ownerId, setOwnerId] = useState<string | undefined>(undefined);
  const [ownersStore] = useState(new CompanyOwnersStore(rootStore, accountId));

  useEffect(() => {
    ownersStore.fetchOwnersData();
  }, [ownersStore]);

  const handleOwnerClose = () => {
    setIsOwnerOpen(false);
    ownersStore.data = null;
  };

  const editOwner = (ownerId: string) => {
    setOwnerId(ownerId);
    setIsOwnerOpen(true);
  };

  const handleRefresh = () => {
    ownersStore.fetchOwnersData();
  };

  const [confirmDialog, setConfirmDialog] = useState<string | undefined>(undefined);

  const listOwners = (data: CompanyOwnersData) => {
    return (
      <>
        {data.owners.map((owner, i) => {
          return (
            <React.Fragment key={owner.id}>
              <Typography variant="h3">
                {i + 1}. {t('contract_aml_legal_owner')}
                <IconButton size="medium" className="accountEdit-button" onClick={() => editOwner(owner.id)}>
                  <EditIcon fontSize="inherit" />
                </IconButton>
                <IconButton size="medium" className="account-delete-button" onClick={() => setConfirmDialog(owner.id)}>
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Typography>
              <Grid container>
                <Grid item xs={4}>
                  <FormTextField
                    name={`position`}
                    label={t('general_relationship')}
                    value={t(`options_relationship_${owner.relationship}`)}
                    fast={false}
                    readOnly
                  />
                </Grid>
                <Grid item xs={8} />
                <Grid item xs={4}>
                  <FormTextField
                    name={`firstname`}
                    label={t('general_name')}
                    value={owner.firstname}
                    fast={false}
                    readOnly
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormTextField
                    name={`lastname`}
                    label={t('general_surname')}
                    value={owner.lastname}
                    fast={false}
                    readOnly
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormTextField
                    name={`gender`}
                    label={t('general_gender')}
                    value={t(`gender_${owner.gender}`)}
                    fast={false}
                    readOnly
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormTextField
                    name={`birthDate`}
                    label={t('general_birthDate')}
                    value={sqlDateToLabel(owner.birthDate, 'dd. MM. yyyy')}
                    fast={false}
                    readOnly
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormTextField
                    name={`placeOfBirth`}
                    label={t('general_placeOfBirth')}
                    value={owner.placeOfBirth}
                    fast={false}
                    readOnly
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormTextField
                    name={`citizenship`}
                    label={t('general_citizenship')}
                    value={t(`country_${owner.citizenship}`)}
                    fast={false}
                    readOnly
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormTextField
                    name="taxResidency"
                    label={t('contract_taxInfo_taxResidency')}
                    value={t(`country_${owner.taxResidency}`)}
                    fast={false}
                    readOnly
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h3">{t('contract_permanentAddress')}</Typography>
              </Grid>

              <Address
                prefix=""
                addressType="address"
                address={owner.address}
                countries={data.options.countryCode}
                country={owner.address.country}
                readOnly
              />
              <Grid container>
                <Grid item xs={12} />
              </Grid>
            </React.Fragment>
          );
        })}
        {data.addingEnabled && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                id="additional-target-button"
                onClick={() => {
                  setOwnerId(undefined);
                  setIsOwnerOpen(true);
                }}
              >
                {t('contract_aml_legal_owner_add')}
              </Button>
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  return ownersStore.ownersData ? (
    <Container maxWidth="md">
      <Background inside>
        <CompanyOwnerModal
          accountId={accountId}
          opened={isOwnerOpen}
          handleClose={handleOwnerClose}
          ownerId={ownerId}
          ownersStore={ownersStore}
          refresh={handleRefresh}
          options={ownersStore.ownersData.options}
        />

        <ConfirmDialog
          noHeader
          isOpen={confirmDialog !== undefined}
          confirm={async () => {
            await ownersStore?.deleteOwner(confirmDialog!);
            setConfirmDialog(undefined);
            handleRefresh();
          }}
          close={() => {
            setConfirmDialog(undefined);
          }}
        >
          {t('contract_aml_legal_owner_deleteConfirm')}
        </ConfirmDialog>

        <Typography variant="h1">{t('contract_aml_legal_owners')}</Typography>
        <Typography
          dangerouslySetInnerHTML={{
            __html: t('contract_aml_legal_ownersInfo', { interpolation: { escapeValue: false } }),
          }}
        />

        {listOwners(ownersStore.ownersData)}
      </Background>
    </Container>
  ) : null;
};

export default compose(inject('rootStore'), observer)(CompanyOwners);
